
/**
 * モックAPI用認証システム（開発用）
 * PHP サーバーが利用できない場合の代替案
 */

interface MockUser {
  id: number;
  username: string;
  password: string;
  role: string;
  email: string;
}

const MOCK_USERS: Record<string, MockUser> = {
  admin: { id: 1, username: 'admin', password: '6yhnmju7!', role: 'admin', email: 'admin@nrentacar.com' },
  staff: { id: 2, username: 'staff', password: '1qazxsw2#', role: 'staff', email: 'staff@nrentacar.com' },
  readonly: { id: 3, username: 'readonly', password: 'asdqwe123', role: 'readonly', email: 'readonly@nrentacar.com' }
};

export class MockAuthService {
  private secretKey: string;

  constructor() {
    this.secretKey = 'mock-jwt-secret-key';
  }

  async login(username: string, password: string): Promise<any> {
    // 2秒の遅延をシミュレート
    await new Promise(resolve => setTimeout(resolve, 1000));

    const user = MOCK_USERS[username];

    if (!user || user.password !== password) {
      throw new Error('ユーザー名またはパスワードが正しくありません');
    }

    const token = this.generateMockJWT(user);
    const userData = {
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role
    };

    return {
      success: true,
      message: 'ログインしました',
      data: {
        token: token,
        user: userData
      }
    };
  }

  async verify(token: string): Promise<any> {
    // 1秒の遅延をシミュレート
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      const payload = this.decodeMockJWT(token);
      const user = MOCK_USERS[payload.username];

      if (!user) {
        throw new Error('Invalid user');
      }

      return {
        success: true,
        valid: true,
        data: {
          user: {
            id: user.id,
            username: user.username,
            email: user.email,
            role: user.role
          },
          token_info: {
            issued_at: new Date(payload.iat * 1000).toISOString(),
            expires_at: new Date(payload.exp * 1000).toISOString(),
            remaining_seconds: payload.exp - Math.floor(Date.now() / 1000)
          }
        }
      };
    } catch (error) {
      return {
        success: false,
        valid: false,
        message: 'Invalid token'
      };
    }
  }

  async logout(): Promise<any> {
    await new Promise(resolve => setTimeout(resolve, 200));

    return {
      success: true,
      message: 'ログアウトしました'
    };
  }

  generateMockJWT(user: MockUser): string {
    const header = { typ: 'JWT', alg: 'HS256' };
    const payload = {
      iss: 'nrentacar-mock',
      aud: 'nrentacar-frontend',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60), // 24時間
      user_id: user.id,
      username: user.username,
      email: user.email,
      role: user.role
    };

    // 簡単なBase64エンコード（実際のJWTではありませんが、テスト用）
    const encodedHeader = btoa(JSON.stringify(header));
    const encodedPayload = btoa(JSON.stringify(payload));
    const signature = btoa(`${encodedHeader}.${encodedPayload}.${this.secretKey}`);

    return `${encodedHeader}.${encodedPayload}.${signature}`;
  }

  decodeMockJWT(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }

    const payload = JSON.parse(atob(parts[1]));

    // 有効期限チェック
    if (payload.exp < Math.floor(Date.now() / 1000)) {
      throw new Error('Token expired');
    }

    return payload;
  }
}

export default MockAuthService;
